import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom, timeout } from 'rxjs';

import { AuthService } from '../services/auth/authservice';
import { isAuthExpired, redirectToLogin } from '../interceptors/auth-expired.interceptor';
import { OrganizationOnboardingService } from '../services/organization/organization-summary';
import { SubscriptionService } from '../services/subscriptions/subscription-service';

export const authenticatedRootRedirectGuard: CanActivateFn = async () => {
  if (typeof window === 'undefined') {
    return true;
  }

  const auth = inject(AuthService);
  const router = inject(Router);
  const organizationService = inject(OrganizationOnboardingService);
  const subscriptionService = inject(SubscriptionService);

  const cachedSession = auth.getSession();
  if (!cachedSession?.authenticated && !auth.hasAuthPresenceHint()) {
    return true;
  }

  const session = await auth.ensureSession({ allowRefresh: false });
  if (!session?.authenticated) {
    return true;
  }

  auth.startSessionHeartbeat();

  const accountType = String(session.accountType ?? '').toUpperCase();

  if (accountType === 'TENANT') {
    window.location.href = auth.tenantDashboardUrl();
    return false;
  }

  const canOwnWorkspace = accountType === 'LANDLORD' || accountType === 'PROPERTY_MANAGER';

  let orgContext: Awaited<ReturnType<typeof firstValueFrom<any>>> | null = null;
  let orgContextLoaded = false;
  try {
    orgContext = await firstValueFrom(
      organizationService.getOrganizationContext().pipe(timeout(8000)),
    );
    orgContextLoaded = true;
  } catch (error) {
    console.error('[RootRedirect] failed to load organization context', error);

    if (isAuthExpired(error)) {
      const refreshedSession = await auth.ensureSession();
      if (!refreshedSession?.authenticated) {
        redirectToLogin();
        return false;
      }
    }
  }

  const organizations = orgContext?.organizations ?? [];

  if (canOwnWorkspace) {
    try {
      const [ownerInvitations, staffInvitations] = await Promise.all([
        firstValueFrom(organizationService.getMyOwnerInvitations().pipe(timeout(8000))).catch(() => []),
        firstValueFrom(organizationService.getMyStaffInvitations().pipe(timeout(8000))).catch(() => []),
      ]);
      if ([...(ownerInvitations ?? []), ...(staffInvitations ?? [])].length > 0) {
        return router.createUrlTree(['/select-workspace']);
      }
    } catch {
      // Fall through to normal workspace/subscription routing.
    }
  }

  if (canOwnWorkspace && organizations.length > 1) {
    return router.createUrlTree(['/select-workspace']);
  }

  if (orgContext?.hasMembership && orgContext?.defaultOrgSubscriptionActive) {
    window.location.href = auth.backofficeDashboardUrl();
    return false;
  }

  if (canOwnWorkspace && orgContextLoaded && orgContext?.hasMembership) {
    return router.createUrlTree(['/select-workspace']);
  }

  if (canOwnWorkspace && !orgContextLoaded) {
    return router.createUrlTree(['/select-workspace']);
  }

  if (!canOwnWorkspace) {
    return true;
  }

  let subscriptionActive = false;
  try {
    const status = await firstValueFrom(
      subscriptionService.getMySubscriptionStatus().pipe(timeout(8000)),
    );
    subscriptionActive = !!status?.active;
  } catch (error) {
    console.error('[RootRedirect] failed to load subscription status', error);

    if (isAuthExpired(error)) {
      const refreshedSession = await auth.ensureSession();
      if (!refreshedSession?.authenticated) {
        redirectToLogin();
        return false;
      }
    }

    return router.createUrlTree(['/select-workspace']);
  }

  if (!subscriptionActive) {
    if (canOwnWorkspace && organizations.length > 0) {
      return router.createUrlTree(['/select-workspace']);
    }

    return router.createUrlTree(['/subscription/select']);
  }

  if (orgContextLoaded && orgContext?.hasMembership === false) {
    return router.createUrlTree(['/organization/create']);
  }

  return router.createUrlTree(['/select-workspace']);
};
